import { getCommonsDateTime } from 'saddlebag-grappler';
import { getQueryStringParams } from 'saddlebag-url';
import { FunnelEventsTracking } from 'saddlebag-user-tracking-events';

import { logErrorWrapper } from '../../../../services/logWrapper';

import type {
  DateSelectionState,
  RouteState,
  State,
} from '../../selectors/types';

const buildLocation = (id: string) => ({
  location_attribute: {
    location_attribute_encoding: 'SS_GEO1_ID',
    location_id: id,
  },
});

const buildDate = (date: Date) => {
  const localDateMillis = date.getTime() - date.getTimezoneOffset() * 60000;
  return getCommonsDateTime(localDateMillis, false, 'MONTH');
};

const buildSearchDetail = ({
  dateSelection,
  route,
}: {
  dateSelection: DateSelectionState;
  route: RouteState;
}) => {
  if (dateSelection.isReturn && dateSelection.selectedInbound) {
    return {
      return: {
        leg1: {
          origin: buildLocation(route.originId),
          destination: buildLocation(route.destinationId),
          ...(dateSelection.wholeYear
            ? { is_cheapest_month: true }
            : { date: buildDate(dateSelection.selectedOutbound) }),
        },
        leg2: {
          origin: buildLocation(route.destinationId),
          destination: buildLocation(route.originId),
          ...(dateSelection.wholeYear
            ? { is_cheapest_month: true }
            : { date: buildDate(dateSelection.selectedInbound) }),
        },
      },
    };
  }

  return {
    one_way: {
      leg: {
        origin: buildLocation(route.originId),
        destination: buildLocation(route.destinationId),
        ...(dateSelection.wholeYear
          ? { is_cheapest_month: true }
          : { date: buildDate(dateSelection.selectedOutbound) }),
      },
    },
  };
};

const getPassengerCounts = () => {
  const params = getQueryStringParams();
  const adults = params.adultsv2;
  const children = params.childrenv2 ? params.childrenv2.split('|') : [];
  return {
    group_model: 'ADULT_18_PLUS',
    adult_count: parseInt(adults, 10),
    child_count: children.length,
    non_adult_ages: children.map((age: string) => parseInt(age, 10)),
  };
};

const getNearbyAirportFlags = () => {
  const params = getQueryStringParams();
  return {
    is_origin_nearby_airport_selected:
      (params.outboundaltsenabled || '').toLowerCase() === 'true',
    is_destination_nearby_airport_selected:
      (params.inboundaltsenabled || '').toLowerCase() === 'true',
  };
};

const buildSearchObject = (search: State) => ({
  cabin_class: 'ECONOMY',
  passenger_group: {
    ...getPassengerCounts(),
  },
  is_direct_only_selected: search.directnessFilter.filtering,
  ...getNearbyAirportFlags(),
  ...buildSearchDetail(search),
});

const buildTripDates = ({
  dateSelection,
}: {
  dateSelection: DateSelectionState;
}) => {
  const { isReturn, selectedInbound, selectedOutbound } = dateSelection;
  if (isReturn && selectedInbound) {
    return {
      start_date: buildDate(selectedOutbound),
      end_date: buildDate(selectedInbound),
    };
  }

  return { start_date: buildDate(selectedOutbound) };
};

const buildFunnelSearch = (state: State) => ({
  business_domain: 'FLIGHT',
  origin: buildLocation(state.route.originId),
  destination: buildLocation(state.route.destinationId),
  ...buildTripDates(state),
  flight_search: buildSearchObject(state),
  ...(state.browseData.viewId && { request_id: state.browseData.viewId }),
});

const logFunnelSearch = async (state: State) => {
  const funnelLogger = new FunnelEventsTracking(
    process.env.NODE_ENV === 'production' ? 'public' : 'public-sandbox',
    'month-view',
  );
  try {
    await funnelLogger.track('clients.Search', buildFunnelSearch(state));
  } catch (err) {
    if (err instanceof Error) {
      logErrorWrapper(err, {
        component: 'funnelEventLogger',
        message: `clients.Search - ${err.message}`,
      });
    } else {
      const error = new Error(String(err));
      logErrorWrapper(error, {
        component: 'funnelEventLogger',
        message: `clients.Search - ${error.message}`,
      });
    }
  }
};

export default logFunnelSearch;
